import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase';
import AdminEventPanel from '../components/events/AdminEventPanel';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const { 
  FiCalendar, FiLink, FiImage, FiSave, FiLoader, FiCheckCircle, FiPlus, 
  FiTrash2, FiEdit3, FiType, FiDollarSign, FiClock, FiStar, FiMail, 
  FiUser, FiMessageSquare, FiTag, FiCamera, FiRefreshCw, FiUpload, 
  FiX, FiCheck, FiEye, FiEyeOff, FiRepeat, FiPhone, FiLayers 
} = FiIcons;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [serviceLinks, setServiceLinks] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [teamImage, setTeamImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  // Modals
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const [isUpdatingTeam, setIsUpdatingTeam] = useState(false);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [editingImageId, setEditingImageId] = useState(null);
  const [galleryInput, setGalleryInput] = useState({ src: '', title: '', category: 'Studio' });
  const [isSavingGallery, setIsSavingGallery] = useState(false);

  const fileInputRef = useRef(null);
  const galleryFileRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: ev } = await supabase.from('events_1740480000000').select('*').order('event_date');
      const { data: sl } = await supabase.from('service_links_1740486000000').select('*').order('service_key');
      const { data: gl } = await supabase.from('gallery_1740486000000').select('*').order('created_at', { ascending: false });
      const { data: msg } = await supabase.from('contacts_1740500000000').select('*').order('created_at', { ascending: false });
      const { data: set } = await supabase.from('settings_1740580000000').select('*').eq('key', 'team_image').single();

      setEvents(ev || []);
      setServiceLinks(sl || []);
      setGallery(gl || []);
      setMessages(msg || []);
      setTeamImage(set?.value || '');
      setPhotoUrlInput(set?.value || '');
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    setLoading(false);
  };

  const handleOpenGalleryModal = (img = null) => {
    if (img) {
      setEditingImageId(img.id);
      setGalleryInput({ src: img.src, title: img.title || '', category: img.category || 'Studio' });
    } else {
      setEditingImageId(null);
      setGalleryInput({ src: '', title: '', category: 'Studio' });
    }
    setIsGalleryModalOpen(true);
  };

  const deleteGalleryImage = async (id) => {
    if (!confirm("Are you sure you want to permanently delete this image?")) return;
    try {
      const { error } = await supabase.from('gallery_1740486000000').delete().eq('id', id);
      if (error) throw error;
      setGallery(prev => prev.filter(img => img.id !== id));
    } catch (err) {
      alert("Delete failed: " + err.message);
      await fetchData();
    }
  };

  const handleSaveGalleryImage = async () => {
    if (!galleryInput.src) return;
    setIsSavingGallery(true);
    try {
      if (editingImageId) {
        const { error } = await supabase
          .from('gallery_1740486000000')
          .update({
            src: galleryInput.src,
            title: galleryInput.title,
            category: galleryInput.category,
            created_at: new Date().toISOString()
          })
          .eq('id', editingImageId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('gallery_1740486000000')
          .insert([{ ...galleryInput, created_at: new Date().toISOString() }]);
        if (error) throw error;
      }
      setIsGalleryModalOpen(false);
      setGalleryInput({ src: '', title: '', category: 'Studio' });
      await fetchData();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setIsSavingGallery(false);
    }
  };

  const handleFileUpload = (e, setInput) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof setInput === 'function') setInput(reader.result);
      else setGalleryInput(prev => ({ ...prev, src: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const saveTeamPhoto = async () => {
    setIsUpdatingTeam(true);
    try {
      const { error } = await supabase
        .from('settings_1740580000000')
        .update({ value: photoUrlInput, updated_at: new Date().toISOString() })
        .eq('key', 'team_image');
      if (error) throw error;
      setTeamImage(photoUrlInput);
      setIsPhotoModalOpen(false);
    } catch (err) {
      alert("Update failed: " + err.message);
    } finally {
      setIsUpdatingTeam(false);
    }
  };

  const toggleMessageRead = async (id, currentStatus) => {
    try {
      await supabase.from('contacts_1740500000000').update({ is_read: !currentStatus }).eq('id', id);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteMessage = async (id) => {
    if (!confirm("Delete this inquiry?")) return;
    await supabase.from('contacts_1740500000000').delete().eq('id', id);
    fetchData();
  };

  const updateService = async (key, formData) => {
    setSavingKey(key);
    try {
      const { features_raw, ...dbData } = formData;
      const featuresArray = features_raw ? features_raw.split(',').map(f => f.trim()).filter(f => f !== '') : [];
      const { error } = await supabase
        .from('service_links_1740486000000')
        .update({ ...dbData, features: featuresArray, updated_at: new Date().toISOString() })
        .eq('service_key', key);
      if (error) throw error;
      await fetchData();
      setTimeout(() => setSavingKey(null), 2000);
    } catch (err) {
      console.error("Service update error:", err);
      alert("Failed to save: " + err.message);
      setSavingKey(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <SafeIcon icon={FiLoader} className="w-12 h-12 text-rose-500 animate-spin mb-4" />
      <p className="text-gray-500 font-medium tracking-wide">Entering Command Center...</p>
    </div>
  );

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Command Center</h1>
            <p className="text-gray-500 mt-1">Manage your studio content and inquiries</p>
          </div>
          <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-gray-200 mt-6 md:mt-0 overflow-x-auto max-w-full">
            {[
              { id: 'events', icon: FiCalendar, label: 'Events' },
              { id: 'services', icon: FiEdit3, label: 'Services' },
              { id: 'gallery', icon: FiImage, label: 'Gallery' },
              { id: 'messages', icon: FiMail, label: 'Inquiries' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-rose-500 text-white shadow-md' : 'text-gray-500 hover:text-rose-500 hover:bg-rose-50'}`}
              >
                <SafeIcon icon={tab.icon} />
                <span>{tab.label}</span>
                {tab.id === 'messages' && unreadCount > 0 && (
                  <span className={`ml-2 px-2 py-0.5 text-[10px] rounded-full ${activeTab === 'messages' ? 'bg-white text-rose-500' : 'bg-rose-500 text-white'}`}>
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {activeTab === 'events' && (
            <AdminEventPanel
              events={events}
              onAdd={async (e) => { await supabase.from('events_1740480000000').insert([e]); fetchData(); }}
              onUpdate={async (id, e) => { await supabase.from('events_1740480000000').update(e).eq('id', id); fetchData(); }}
              onDelete={async (id) => { if (confirm("Delete this event?")) { await supabase.from('events_1740480000000').delete().eq('id', id); fetchData(); } }}
            />
          )}

          {activeTab === 'services' && (
            <div className="grid grid-cols-1 gap-8">
              {serviceLinks.map(link => (
                <ServiceEditorCard
                  key={link.id}
                  link={link}
                  isSaving={savingKey === link.service_key}
                  onSave={(data) => updateService(link.service_key, data)}
                />
              ))}
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-rose-100 bg-gradient-to-br from-white to-rose-50/30">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="w-48 h-60 rounded-3xl overflow-hidden shadow-lg border-4 border-white flex-shrink-0 bg-gray-100">
                    <img src={teamImage} alt="Founders" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center px-3 py-1 bg-rose-100 text-rose-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                      <SafeIcon icon={FiCamera} className="mr-2" /> Team Identity
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Founders Photo</h2>
                    <p className="text-gray-500 mb-6 max-w-md">Update the primary photo of Iga and Iman shown on the homepage.</p>
                    <button onClick={() => setIsPhotoModalOpen(true)} className="bg-rose-500 text-white px-8 py-3 rounded-2xl font-bold flex items-center space-x-2 hover:bg-rose-600 shadow-lg shadow-rose-200 transition-all">
                      <SafeIcon icon={FiRefreshCw} /> <span>Update Photo</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-gray-100">
                <div className="flex justify-between items-center mb-10">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Public Gallery</h2>
                    <p className="text-sm text-gray-500">Manage images displayed in the "Gallery & Stories" section</p>
                  </div>
                  <button onClick={() => handleOpenGalleryModal()} className="bg-gray-900 text-white px-6 py-3 rounded-2xl font-bold flex items-center space-x-2 hover:bg-black transition-all">
                    <SafeIcon icon={FiPlus} /> <span>Add New Story</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                  {gallery.map(img => (
                    <div key={img.id} className="group relative aspect-square rounded-3xl overflow-hidden shadow-md bg-gray-100 border border-gray-100 hover:border-rose-200 transition-all">
                      <img src={img.src} alt={img.title} className="w-full h-full object-cover" />
                      <div className="absolute top-3 right-3 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button onClick={() => deleteGalleryImage(img.id)} className="bg-rose-500 hover:bg-rose-600 text-white p-2.5 rounded-xl shadow-lg transition-all flex items-center justify-center" title="Delete Permanently">
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleOpenGalleryModal(img)} className="bg-white hover:bg-gray-50 text-gray-800 p-2.5 rounded-xl shadow-lg transition-all flex items-center justify-center" title="Replace Image">
                          <SafeIcon icon={FiRepeat} className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-[10px] font-black uppercase tracking-widest truncate">{img.title || 'Untitled'}</p>
                        <p className="text-rose-400 text-[8px] font-bold uppercase tracking-widest">{img.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="space-y-6">
              <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100">
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Booking Inquiries</h2>
                    <p className="text-gray-500">Manage leads from your contact form</p>
                  </div>
                  {unreadCount > 0 && <span className="bg-rose-100 text-rose-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">{unreadCount} New Requests</span>}
                </div>
                <div className="space-y-4">
                  {messages.map(msg => (
                    <div key={msg.id} className={`p-6 md:p-8 rounded-3xl border transition-all ${msg.is_read ? 'bg-gray-50/50 border-gray-100 opacity-75' : 'bg-white border-rose-100 shadow-md'}`}>
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-4">
                            {!msg.is_read && <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></span>}
                            
                            {/* Personal Info Chips */}
                            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center">
                              <SafeIcon icon={FiUser} className="text-rose-500 mr-2" />
                              <span className="font-bold text-gray-800">{msg.name}</span>
                            </div>
                            
                            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center">
                              <SafeIcon icon={FiMail} className="text-purple-500 mr-2" />
                              <span className="text-gray-600 font-medium">{msg.email}</span>
                            </div>

                            {msg.phone && (
                              <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center">
                                <SafeIcon icon={FiPhone} className="text-blue-500 mr-2" />
                                <span className="text-gray-600 font-medium">{msg.phone}</span>
                              </div>
                            )}

                            {msg.event_type && (
                              <div className="bg-rose-50 px-4 py-2 rounded-xl shadow-sm border border-rose-100 flex items-center">
                                <SafeIcon icon={FiLayers} className="text-rose-600 mr-2" />
                                <span className="text-rose-700 font-bold text-sm tracking-tight">{msg.event_type}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="bg-white/50 p-6 rounded-2xl border border-gray-100 italic text-gray-600">
                            {msg.message}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end space-y-3">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{format(new Date(msg.created_at), 'MMM d, h:mm a')}</span>
                          <div className="flex items-center space-x-2">
                            <button onClick={() => toggleMessageRead(msg.id, msg.is_read)} className={`p-3 rounded-xl transition-all flex items-center space-x-2 font-bold text-xs ${msg.is_read ? 'text-gray-400 hover:text-rose-500' : 'bg-green-500 text-white shadow-lg'}`}>
                              <SafeIcon icon={msg.is_read ? FiEyeOff : FiCheck} />
                              <span>{msg.is_read ? 'Read' : 'Mark Read'}</span>
                            </button>
                            <button onClick={() => deleteMessage(msg.id)} className="p-3 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all">
                              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Team Photo Update Modal */}
      <AnimatePresence>
        {isPhotoModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="bg-white rounded-[2.5rem] w-full max-w-xl overflow-hidden shadow-2xl">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">Update Team Photo</h3>
                <button onClick={() => setIsPhotoModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="p-10 space-y-8">
                <div className="flex justify-center">
                  <div className="w-48 h-60 rounded-3xl overflow-hidden ring-4 ring-rose-50 shadow-inner bg-gray-50 flex items-center justify-center leading-none">
                    {photoUrlInput ? <img src={photoUrlInput} className="w-full h-full object-cover" alt="Preview" /> : <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-200" />}
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Upload File</label>
                    <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e, setPhotoUrlInput)} className="hidden" accept="image/*" />
                    <button onClick={() => fileInputRef.current?.click()} className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center space-x-3 text-gray-500 hover:border-rose-400 hover:text-rose-500 transition-all font-bold">
                      <SafeIcon icon={FiUpload} /> <span>Choose local image</span>
                    </button>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Image URL</label>
                    <input className="w-full pl-4 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-rose-500 outline-none" placeholder="https://..." value={photoUrlInput} onChange={(e) => setPhotoUrlInput(e.target.value)} />
                  </div>
                </div>
                <button onClick={saveTeamPhoto} disabled={isUpdatingTeam || !photoUrlInput} className="w-full bg-gradient-to-r from-rose-500 to-purple-600 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-2 shadow-xl disabled:opacity-50">
                  {isUpdatingTeam ? <FiLoader className="animate-spin" /> : <FiCheckCircle />}
                  <span>Save Team Photo</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Save/Replace Modal */}
      <AnimatePresence>
        {isGalleryModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="bg-white rounded-[2.5rem] w-full max-w-xl overflow-hidden shadow-2xl">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">{editingImageId ? 'Replace Image' : 'Add New Story'}</h3>
                <button onClick={() => setIsGalleryModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="p-10 space-y-6">
                <div className="w-full aspect-video rounded-3xl overflow-hidden ring-4 ring-gray-50 shadow-inner bg-gray-100 flex items-center justify-center relative">
                  {galleryInput.src ? <img src={galleryInput.src} className="w-full h-full object-cover" alt="Preview" /> : (
                    <div className="text-center">
                      <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">No Image Chosen</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Description</label>
                    <input className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-rose-500" placeholder="Title" value={galleryInput.title} onChange={e => setGalleryInput({ ...galleryInput, title: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest ml-1">Category</label>
                    <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-rose-500" value={galleryInput.category} onChange={e => setGalleryInput({ ...galleryInput, category: e.target.value })}>
                      <option value="Studio">Studio</option>
                      <option value="Artwork">Artwork</option>
                      <option value="Team">Team</option>
                      <option value="Private Event">Private Event</option>
                      <option value="Outdoor Edition">Outdoor Edition</option>
                      <option value="Unique Experience">Unique Experience</option>
                      <option value="Team Building">Team Building</option>
                      <option value="Workshop">Workshop</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <input type="file" ref={galleryFileRef} onChange={(e) => handleFileUpload(e)} className="hidden" accept="image/*" />
                  <button onClick={() => galleryFileRef.current?.click()} className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center space-x-3 text-gray-500 hover:border-rose-400 hover:text-rose-500 transition-all font-bold">
                    <SafeIcon icon={FiUpload} /> <span>{galleryInput.src ? 'Change Source' : 'Upload Image'}</span>
                  </button>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
                    <div className="relative flex justify-center text-[10px] uppercase tracking-widest text-gray-300"><span className="bg-white px-4">OR</span></div>
                  </div>
                  <input className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-rose-500" placeholder="Paste image URL..." value={galleryInput.src} onChange={e => setGalleryInput({ ...galleryInput, src: e.target.value })} />
                </div>
                <button onClick={handleSaveGalleryImage} disabled={isSavingGallery || !galleryInput.src} className="w-full bg-gradient-to-r from-rose-500 to-purple-600 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-2 shadow-xl disabled:opacity-50">
                  {isSavingGallery ? <FiLoader className="animate-spin" /> : <FiCheckCircle />}
                  <span>{editingImageId ? 'Update Entry' : 'Publish Story'}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ServiceEditorCard = ({ link, onSave, isSaving }) => {
  const [formData, setFormData] = useState({
    title: link.title || '',
    description: link.description || '',
    price: link.price || '',
    duration: link.duration || '',
    url: link.url || '',
    is_popular: link.is_popular || false,
    features_raw: (link.features || []).join(',')
  });

  return (
    <div className={`bg-white p-8 rounded-[2.5rem] shadow-lg border-2 transition-all ${formData.is_popular ? 'border-rose-200' : 'border-gray-50'} flex flex-col lg:flex-row gap-8`}>
      <div className="lg:w-1/3">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`p-3 rounded-xl ${formData.is_popular ? 'bg-rose-500 text-white' : 'bg-rose-50 text-rose-500'}`}>
            <SafeIcon icon={formData.is_popular ? FiStar : FiEdit3} />
          </div>
          <h3 className="text-xl font-bold text-gray-800 uppercase tracking-tight">{link.service_key} Card</h3>
        </div>
        <div onClick={() => setFormData({ ...formData, is_popular: !formData.is_popular })} className={`px-4 py-3 rounded-2xl border-2 cursor-pointer flex items-center justify-between transition-all ${formData.is_popular ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-gray-100 text-gray-400 hover:border-rose-200'}`}>
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiStar} className={formData.is_popular ? 'fill-current' : ''} />
            <span className="font-bold text-sm">Most Popular Tag</span>
          </div>
        </div>
      </div>
      <div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <input className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-rose-500" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Title" />
        <input className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-rose-500" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} placeholder="Price" />
        <input className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-rose-500" value={formData.duration} onChange={e => setFormData({ ...formData, duration: e.target.value })} placeholder="Duration" />
        <input className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-rose-500" value={formData.url} onChange={e => setFormData({ ...formData, url: e.target.value })} placeholder="Booking Link" />
        <textarea rows="2" className="w-full p-4 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-rose-500 resize-none md:col-span-2" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Description" />
        <textarea rows="2" className="w-full p-4 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-rose-500 resize-none md:col-span-2" value={formData.features_raw} onChange={e => setFormData({ ...formData, features_raw: e.target.value })} placeholder="Features (Comma Separated)" />
        <button onClick={() => onSave(formData)} className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 transition-all shadow-sm md:col-span-2 ${isSaving ? 'bg-green-500 text-white' : 'bg-gray-900 text-white hover:bg-black'}`}>
          <SafeIcon icon={isSaving ? FiCheckCircle : FiSave} />
          <span>{isSaving ? 'Changes Saved!' : 'Save Card Changes'}</span>
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;